import { normalizeMoneyValue } from "./gtmDataLayer";

const DEFAULT_CURRENCY = "CHF";
const DEFAULT_ITEM_BRAND = "Flyspa";
const DEFAULT_ITEM_LIST_ID = "massage_services";
const DEFAULT_ITEM_LIST_NAME = "massage_list_name";

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getLocalizedValue = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    const candidate =
      value.fr ??
      value.en ??
      value.de ??
      value.it ??
      value.label ??
      value.title ??
      value.name;

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }

    const firstString = Object.values(value).find(
      (entry) => typeof entry === "string" && entry.trim()
    );
    return typeof firstString === "string" ? firstString.trim() : undefined;
  }
  return undefined;
};

const toPositiveInteger = (value, fallback = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed;
};

const toIdString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const toTrimmedString = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (typeof value === "object") {
    return getLocalizedValue(value);
  }
  return undefined;
};

const hasObjectEntries = (value) =>
  value !== null
  && typeof value === "object"
  && !Array.isArray(value)
  && Object.keys(value).length > 0;

const firstString = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = toTrimmedString(candidate);
    if (normalized) return normalized;
  }
  return undefined;
};

const firstObject = (...candidates) => {
  for (const candidate of candidates) {
    if (hasObjectEntries(candidate)) return candidate;
  }
  return {};
};

const buildStreetValue = (address = {}, fallbackStreet, fallbackStreetNumber) => {
  const street = firstString(
    address?.street,
    address?.address_line_1,
    address?.address_line1,
    address?.line1,
    fallbackStreet
  );
  const streetNumber = firstString(
    address?.street_number,
    address?.streetnumber,
    address?.house_number,
    address?.number,
    fallbackStreetNumber
  );

  if (street && streetNumber) {
    const normalizedStreet = street.toLowerCase();
    const normalizedStreetNumber = streetNumber.toLowerCase();
    if (normalizedStreet.includes(normalizedStreetNumber)) {
      return street;
    }
    return `${street} ${streetNumber}`.trim();
  }

  return street || undefined;
};

export const normalizePaymentType = (paymentType) => {
  const label = getLocalizedValue(paymentType);
  if (!label) return undefined;

  const normalizedLabel = label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const normalizedForMatch = normalizedLabel
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const slug = normalizedLabel
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

  const contains = (value) => normalizedForMatch.includes(value);

  // Keep GTM values stable across card brands / gateway labels / locale variants.
  if (contains("twint")) {
    return "twint";
  }

  if (contains("postfinance pay")) {
    return "postfinance_pay";
  }

  if (contains("postfinance e finance") || contains("postfinance e-finance")) {
    return "postfinance_e_finance";
  }

  if (contains("postfinance card") || contains("carte postfinance")) {
    return "carte_postfinance";
  }

  const isCardLike =
    contains("wallee all in one")
    || contains("wallee snapswap all in one")
    || contains("all in one")
    || contains("american express")
    || contains("amex")
    || contains("visa")
    || contains("mastercard")
    || contains("master card")
    || contains("maestro")
    || contains("debit")
    || contains("credit")
    || contains("carte de credit")
    || contains("carte de debit")
    || contains("carte bancaire")
    || contains("credit card")
    || contains("debit card")
    || contains("card");

  if (isCardLike) {
    return "carte_credit_debit";
  }

  const aliases = {
    carte_credit_debit: "carte_credit_debit",
    carte_postfinance: "carte_postfinance",
    postfinance_card: "carte_postfinance",
    postfinance_pay: "postfinance_pay",
    twint: "twint",
    postfinance_e_finance: "postfinance_e_finance",
    postfinance_efinance: "postfinance_e_finance",
  };

  return aliases[slug] || slug;
};

export const computeTaxFromTtc = (ttcValue, taxRate = 8.1) => {
  const amount = Number(ttcValue);
  const rate = Number(taxRate);
  if (!Number.isFinite(amount) || amount < 0) return undefined;
  if (!Number.isFinite(rate) || rate <= 0) return undefined;

  const divisor = 1 + rate / 100;
  const tax = amount - amount / divisor;
  return normalizeMoneyValue(tax);
};

export const buildUserDataObject = ({
  user,
  client,
  address,
  booking,
  promotion,
  phoneNumber,
  emailAddress,
  firstName,
  lastName,
  userId,
  isConnectedUser,
} = {}) => {
  const bookingObject = firstObject(booking);
  const promotionObject = firstObject(promotion);
  const userObject = firstObject(user);
  const connectedUser = isConnectedUser === true;
  const clientObject = firstObject(
    client,
    bookingObject?.client,
    promotionObject?.client
  );
  const userPrimaryAddress = firstObject(
    userObject?.primary_address,
    userObject?.primaryAddress
  );
  const clientPrimaryAddress = firstObject(
    clientObject?.primary_address,
    clientObject?.primaryAddress
  );
  const flowAddress = firstObject(
    address,
    bookingObject?.address,
    promotionObject?.address
  );
  const resolvedAddress = connectedUser
    ? firstObject(
      userPrimaryAddress,
      flowAddress,
      clientPrimaryAddress
    )
    : firstObject(
      flowAddress,
      clientPrimaryAddress,
      userPrimaryAddress
    );
  const fallbackAddress = firstObject(
    flowAddress,
    clientPrimaryAddress,
    userPrimaryAddress
  );

  const resolvedFullName = firstString(
    userObject?.full_name,
    userObject?.fullName,
    userObject?.name,
    clientObject?.full_name,
    clientObject?.fullName,
    clientObject?.name
  );
  const nameParts = resolvedFullName ? resolvedFullName.split(/\s+/).filter(Boolean) : [];
  const inferredFirstName = nameParts.length ? nameParts[0] : undefined;
  const inferredLastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : undefined;

  const userData = {
    city: firstString(
      resolvedAddress?.city,
      fallbackAddress?.city,
      bookingObject?.city,
      promotionObject?.city
    ),
    country: firstString(
      resolvedAddress?.country,
      fallbackAddress?.country,
      bookingObject?.country,
      promotionObject?.country
    ),
    email_address: firstString(
      emailAddress,
      userObject?.email,
      clientObject?.email,
      bookingObject?.email,
      promotionObject?.buyer_email,
      promotionObject?.to_user_email,
      promotionObject?.email
    ),
    first_name: firstString(
      firstName,
      userObject?.first_name,
      userObject?.firstname,
      clientObject?.first_name,
      clientObject?.firstname,
      inferredFirstName
    ),
    last_name: firstString(
      lastName,
      userObject?.last_name,
      userObject?.lastname,
      clientObject?.last_name,
      clientObject?.lastname,
      inferredLastName
    ),
    phone_number: firstString(
      phoneNumber,
      userObject?.phone,
      clientObject?.phone,
      bookingObject?.phone,
      promotionObject?.phone
    ),
    postal_code: firstString(
      resolvedAddress?.postal_code,
      resolvedAddress?.zip_code,
      fallbackAddress?.postal_code,
      fallbackAddress?.zip_code,
      bookingObject?.postal_code,
      bookingObject?.zip_code,
      promotionObject?.postal_code,
      promotionObject?.zip_code
    ),
    region: firstString(
      resolvedAddress?.region,
      resolvedAddress?.state,
      resolvedAddress?.state_code,
      fallbackAddress?.region,
      fallbackAddress?.state,
      fallbackAddress?.state_code,
      bookingObject?.region,
      bookingObject?.state,
      bookingObject?.state_code,
      promotionObject?.region,
      promotionObject?.state,
      promotionObject?.state_code
    ),
    street: buildStreetValue(
      resolvedAddress,
      firstString(bookingObject?.street, promotionObject?.street),
      firstString(
        bookingObject?.street_number,
        bookingObject?.streetnumber,
        promotionObject?.street_number,
        promotionObject?.streetnumber
      )
    ),
    user_id: firstString(
      userId,
      userObject?.id,
      clientObject?.id,
      bookingObject?.client_id,
      promotionObject?.client_id
    ),
  };

  return {
    city: userData.city || "",
    country: userData.country || "",
    email_address: userData.email_address || "",
    first_name: userData.first_name || "",
    last_name: userData.last_name || "",
    phone_number: userData.phone_number || "",
    postal_code: userData.postal_code || "",
    region: userData.region || "",
    street: userData.street || "",
    user_id: userData.user_id || "",
  };
};

const inferLocationCategory = (context = {}) => {
  const booking = context.booking || {};
  const address = context.address || booking.address || {};
  const hasHotel =
    Boolean(context.hotelId) ||
    Boolean(booking.hotel_id) ||
    Boolean(booking.hotel?.id) ||
    Boolean(address.hotel_id) ||
    Boolean(address.hotel?.id) ||
    (Array.isArray(address.hotel) && address.hotel.length > 0);

  return hasHotel ? "hotel_partenaire" : "a_domicile";
};

const getLocationId = (context = {}) => {
  const booking = context.booking || {};
  const address = context.address || booking.address || {};
  return (
    toIdString(context.locationId) ||
    toIdString(address.place_id) ||
    toIdString(address.id) ||
    toIdString(booking.address_id) ||
    undefined
  );
};

const createItem = (product, index, context = {}, overrides = {}) => {
  if (!product || typeof product !== "object") return null;

  const itemName =
    getLocalizedValue(overrides.itemName) ||
    getLocalizedValue(product.name) ||
    `item_${index + 1}`;
  const itemId =
    toIdString(overrides.itemId) ||
    toIdString(product.id) ||
    toIdString(product.sku) ||
    itemName;
  const itemPrice = normalizeMoneyValue(
    overrides.price ?? product.price ?? product.amount
  );
  const itemQuantity = toPositiveInteger(overrides.quantity ?? 1, 1);
  const itemDiscount = normalizeMoneyValue(
    overrides.discount ??
      product.discount ??
      product.discount_amount ??
      product.discount_value ??
      product.promotion_discount ??
      product.promo_discount
  );
  const itemCategory2 =
    getLocalizedValue(overrides.itemCategory2) ||
    getLocalizedValue(product?.category?.title) ||
    getLocalizedValue(product?.category?.name);
  const itemListName =
    getLocalizedValue(overrides.itemListName) ||
    getLocalizedValue(product?.domain?.title) ||
    DEFAULT_ITEM_LIST_NAME;

  return {
    item_id: itemId,
    item_name: itemName,
    price: itemPrice ?? 0,
    quantity: itemQuantity,
    discount: itemDiscount ?? 0,
    item_brand: getLocalizedValue(overrides.itemBrand) || DEFAULT_ITEM_BRAND,
    item_category: inferLocationCategory(context),
    item_category2: itemCategory2,
    item_list_id: toIdString(overrides.itemListId) || DEFAULT_ITEM_LIST_ID,
    item_list_name: itemListName,
    location_id: getLocationId(context),
    index,
  };
};

export const buildItemsFromSelectedServices = (selectedServices, context = {}) =>
  asArray(selectedServices)
    .map((service, index) => {
      const product = service?.product || service?.service || service;
      return createItem(product, index, context, {
        itemCategory2:
          service?.subCategory?.title ||
          service?.category?.title ||
          service?.category?.name,
        itemListName: service?.domain?.title,
      });
    })
    .filter(Boolean);

export const buildItemsFromBooking = (booking, context = {}) => {
  const products =
    asArray(booking?.products).length > 0
      ? asArray(booking?.products)
      : asArray(booking?.product);

  return products
    .map((product, index) =>
      createItem(product, index, { ...context, booking }, {})
    )
    .filter(Boolean);
};

export const calculateValueFromItems = (items = []) => {
  const total = asArray(items).reduce((sum, item) => {
    const price = Number(item?.price);
    const quantity = Number(item?.quantity ?? 1);
    if (!Number.isFinite(price) || !Number.isFinite(quantity)) return sum;
    return sum + price * quantity;
  }, 0);

  return normalizeMoneyValue(total);
};

export const resolveBookingTransactionId = (booking, fallbackId) => {
  const orderId = toIdString(booking?.order_id);
  if (orderId) return orderId;

  const bookingId = toIdString(booking?.id ?? fallbackId);
  return bookingId || undefined;
};

export const extractPaymentMethodFromTransactions = (transactions) => {
  const list = asArray(transactions);
  if (!list.length) return undefined;

  const priorityStatuses = new Set(["FULFILL", "COMPLETED", "AUTHORIZED"]);
  const scored = list
    .map((transaction) => {
      const status = String(transaction?.status || "").toUpperCase();
      const dateValue = transaction?.succeeded_at || transaction?.updated_at || transaction?.created_at;
      const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
      return {
        transaction,
        score: priorityStatuses.has(status) ? 1 : 0,
        timestamp: Number.isFinite(timestamp) ? timestamp : 0,
      };
    })
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      return b.timestamp - a.timestamp;
    });

  const latestTransaction = scored[0]?.transaction;

  return (
    getLocalizedValue(latestTransaction?.payment_method) ||
    getLocalizedValue(latestTransaction?.payment_type) ||
    getLocalizedValue(latestTransaction?.paymentMethod) ||
    getLocalizedValue(latestTransaction?.paymentType) ||
    getLocalizedValue(latestTransaction?.payment_connector_name) ||
    getLocalizedValue(latestTransaction?.paymentConnectorName) ||
    getLocalizedValue(latestTransaction?.processor_configuration_name) ||
    getLocalizedValue(latestTransaction?.processorConfigurationName) ||
    getLocalizedValue(latestTransaction?.payment_connector_configuration?.name) ||
    getLocalizedValue(latestTransaction?.processor_configuration?.name) ||
    undefined
  );
};

export const buildEcommerceObject = ({
  currency = DEFAULT_CURRENCY,
  value,
  transactionId,
  coupon,
  tax,
  shipping,
  paymentType,
  items,
} = {}) => {
  const normalizedItems = asArray(items).filter(Boolean);
  const normalizedCoupon = getLocalizedValue(coupon);

  const ecommerce = {
    currency,
    value: normalizeMoneyValue(value),
    transaction_id: toIdString(transactionId) || undefined,
    coupon: normalizedCoupon ?? "",
    tax: normalizeMoneyValue(tax),
    shipping: normalizeMoneyValue(shipping),
    payment_type: normalizePaymentType(paymentType),
    items: normalizedItems.length ? normalizedItems : undefined,
  };

  return Object.fromEntries(
    Object.entries(ecommerce).filter(([, entry]) => entry !== undefined)
  );
};
